'use strict';
import { NgUtils } from "./utils";
import * as angular from 'angular';

declare var Reflect: any;

export declare interface IDecoratorParameters {
    module: angular.IModule,
    selector: string
}

export declare interface IComponentParameters extends IDecoratorParameters, angular.IComponentOptions {

}

export function Component(parameters: IComponentParameters): ClassDecorator {
    var module = parameters.module;
    var selector = parameters.selector;
    delete parameters.module;
    delete parameters.selector;

    return <TFunction extends Function>(target: TFunction) => {
        parameters.controller = <any>target;
        if (target.prototype._bindings) {
            parameters.bindings = parameters.bindings || {};
            for (var b in target.prototype._bindings) {
                parameters.bindings[b] = target.prototype._bindings[b];
            }
            delete target.prototype._bindings
        }
        module.component(selector, parameters);
        target["_selector"] = selector;
        target["_moduleName"] = module.name;
        return target;
    };
}

export interface IComponent {
    /**
     * Called on each controller after all the controllers on an element have been constructed and had their bindings initialized (and before the pre & post linking functions for the directives on this element). This is a good place to put initialization code for your controller.
     */
    $onInit?(): void

    /**
     * Called whenever one-way bindings are updated. The changesObj is a hash whose keys are the names of the bound properties that have changed, and the values are an object of the form { currentValue, previousValue, isFirstChange() }. Use this hook to trigger updates within a component such as cloning the bound value to prevent accidental mutation of the outer value. 
    */
    $onChanges?(changesObj: { currentValue: any, previousValue: any, isFirstChange: () => any }): void

    /**
     * Called on a controller when its containing scope is destroyed. Use this hook for releasing external resources, watches and event handlers.
     */
    $onDestroy?(): void

    /** 
     * Called after this controller's element and its children have been linked. Similar to the post-link function this hook can be used to set up DOM event handlers and do direct DOM manipulation. Note that child elements that contain templateUrl directives will not have been compiled and linked since they are waiting for their template to load asynchronously and their own compilation and linking has been suspended until that occurs. This hook can be considered analogous to the ngAfterViewInit and ngAfterContentInit hooks in Angular 2. Since the compilation process is rather different in Angular 1 there is no direct mapping and care should be taken when upgrading.
    */
    $postLink?(): void
}


export function Injectable(parameters: IDecoratorParameters): ClassDecorator {
    return <TFunction extends Function>(target: TFunction) => {
        parameters.module.service(parameters.selector, target);
        return target;
    }
}

export interface IPipeParameters extends IDecoratorParameters {
    pure?: boolean
}

export function Pipe(parameters: IPipeParameters): MethodDecorator {
    return <T>(target: Object, propertyKey: string | symbol, descriptor: TypedPropertyDescriptor<T>) => {
        var pipe: Function = target[propertyKey];
        if (parameters.pure !== true) {
            var opipe = pipe;
            pipe = function () {
                var pipefunc = opipe.apply(opipe, arguments);
                pipefunc.$stateful = true;
                return pipefunc;
            }
            if (opipe.$inject !== undefined)
                pipe.$inject = opipe.$inject;
        }
        parameters.module.filter(parameters.selector, pipe);
        return target;
    }
}

export function Directive(parameters: IDecoratorParameters): ClassDecorator {
    return <TFunction extends Function>(target: TFunction) => {
        parameters.module.directive(parameters.selector, NgUtils.Factory(target));
        return target;
    }
}

export function Config({ module }: { module: angular.IModule }): ClassDecorator {
    return <TFunction extends Function>(target: TFunction) => {
        module.config(target);
        return target;
    }
}

/**
 * Declares a data-bound input property.
 *
 * Angular automatically updates data-bound properties during change detection.
 *
 * `Input` takes an optional parameter that specifies the name
 * used when instantiating a component in the template. When not provided,
 * the name of the decorated property is used.
 *
 */
export function Input(bindingPropertyName: string = ""): PropertyDecorator {
    return (target: any, propertyKey: string | symbol) => {
        target._bindings = target._bindings || {};
        target._bindings[propertyKey] = `<${bindingPropertyName}`;
    };
}

/**
 * Declares an event-bound output property.
 *
 * When an output property emits an event, an event handler attached to that event
 * the template is invoked.
 *
 * `Output` takes an optional parameter that specifies the name
 * used when instantiating a component in the template. When not provided,
 * the name of the decorated property is used.
 */
export function Output(bindingPropertyName: string = "", addBuffer = false): any {
    return function outputDecorator(target: Object, propertyKey: string | symbol, propertyDescriptor: TypedPropertyDescriptor<any>): TypedPropertyDescriptor<any> | void {
        target["_bindings"] = target["_bindings"] || {};
        target["_bindings"][propertyKey] = `&${bindingPropertyName || ""}`;
        if (!addBuffer) {
            if (!target[propertyKey])
                target[propertyKey] = () => { };
            return;
        }
        return {
            configurable: false,
            get: (!propertyDescriptor || propertyDescriptor.get) ? function () {
                var val = propertyDescriptor === undefined ? this[`_${propertyKey}`] : propertyDescriptor.get.call(this);
                if (val !== undefined)
                    return val;

                return function () {
                    if (val !== undefined)
                        val.apply(this, arguments);
                    else
                        this[`_${propertyKey}.buffer`] = (this[`_${propertyKey}.buffer`] || []).concat(arguments);
                }
            } : undefined,
            set: (!propertyDescriptor || propertyDescriptor.set) ? function (v: Function) {
                var buffer: IArguments[] = this[`_${propertyKey}.buffer`] || [];
                propertyDescriptor === undefined ? this[`_${propertyKey}`] = v : propertyDescriptor.set.call(this, v);
                if (buffer.length > 0) {
                    for (var b of buffer) {
                        v.apply(this, b);
                    }
                    this[`_${propertyKey}.buffer`] = [];
                }
            } : undefined
        };
    };
}

export type OutputArgs<T> = (args: T) => void;

/**
 * Declares an two-way data-bound property.
 * 
 * Angular automatically updates data-bound properties during change detection.
 *
 * `Binding` takes an optional parameter that specifies the name
 * used when instantiating a component in the template. When not provided,
 * the name of the decorated property is used.
 */
export function Binding(bindingPropertyName: string = ""): PropertyDecorator {
    return (target: any, propertyKey: string | symbol) => {
        target._bindings = target._bindings || {};
        target._bindings[propertyKey] = `=${bindingPropertyName}`;
    };
}

/**
 * Declares a one time bound property.
 *
 * Angular reads the value of the attribute only once on creation
 * 
 * `Attribute` takes an optional parameter that specifies the name
 * used when instantiating a component in the template. When not provided,
 * the name of the decorated property is used.
 */
export function Attribute(bindingPropertyName: string = ""): PropertyDecorator {
    return (target: any, propertyKey: string | symbol) => {
        target._bindings = target._bindings || {};
        target._bindings[propertyKey] = `@${bindingPropertyName}`;
    };
}


export function Inject(type?: string): ParameterDecorator {
    //var typeName = (typeof (type) == 'string') ? type : /^function\s+([\w\$]+)\s*\(/.exec(type.toString())[1]
    return (target: Object, propertyKey: string | symbol, parameterIndex: number) => {
        var paramType: any = "";
        if (Reflect.hasMetadata('design:paramtypes', target))
            paramType = Reflect.getMetadata('design:paramtypes', target)[parameterIndex];
        type = type || paramType.selector || paramType.name
        if (!type)
            throw "Unknown type, nothing to inject"

        var injectable = propertyKey ? <any>target[propertyKey] : <any>target;
        if (!injectable.$inject)
            injectable.$inject = new Array();
        injectable.$inject[parameterIndex] = type;
    };
}

export function Controller(parameters: IDecoratorParameters): ClassDecorator {
    return <TFunction extends Function>(target: TFunction) => {
        parameters.module.controller(parameters.selector, <any>target);
        return target;
    }
}

export function LegacyConstant(parameters: IDecoratorParameters): ClassDecorator {
    return <TFunction extends Function>(target: any) => {
        parameters.module.constant(parameters.selector, new target());
        return target;
    }
}
